import { useCallback, useEffect, useRef, useState } from "react";
import type { PointerEvent as ReactPointerEvent, ReactElement } from "react";
import { ApiError } from "@kundeninfo/api-client";
import type { ApiClient, Anhaenge as AnhaengeStand, Anhangsbezug }
  from "@kundeninfo/api-client";
import { anhangsSchluessel } from "@kundeninfo/api-client";
import { Modal, ModalFuss, useZiehOrdnung, useBestaetigung } from "@kundeninfo/ui";
import { Hinweis, Schalter } from "@kundeninfo/ui/me-design";
import { renderMarkdown } from "../markdown.js";
import { formatDatum } from "../format.js";
import "./Anhaenge.css";

/**
 * Der Kasten „ANHÄNGE" an Angebot, Angebotsvorlage und Auftrag (Spec „Textbausteine als
 * Anhang", 24.09.2026, Abschnitt „Oberfläche").
 *
 * <h3>Ein Baustein für drei Einbauorte</h3>
 * Wie `Zahlplan` und `BelegParteien`: Der Baustein kennt seinen Einbauort nicht, er kennt
 * nur seinen `bezug`. Was er KANN, sagt ihm die Antwort — `eingefroren` heisst „nur
 * lesen", und am Auftrag (`bezug.art === "order"`) gibt es statt der Schreibwege den
 * Schalter „ausblenden". Damit ist derselbe Kasten dreimal eingehängt, ohne eine einzige
 * Verzweigung auf den Einbauort.
 *
 * <h3>Der Kasten ist KEIN Modal — die Bausteinauswahl schon</h3>
 * Ziehen, Pfeile, „Entfernen" und der Ausblenden-Schalter wirken SOFORT auf den Server
 * (dieselbe Mechanik wie die Positionen): Sie haben keinen Entwurf, den ein Verlassen
 * abschliessen müsste. Die AUSWAHL eines Bausteins ist dagegen eine Entscheidung mit zwei
 * Ausgängen und steht deshalb im strikten Modalpanel aus `@kundeninfo/ui` (Hausregel
 * „Modal-Bearbeitung").
 *
 * <h3>Reihenfolge: primär Ziehen, Pfeile als gleichwertige Alternative (Entscheid E8)</h3>
 * Beide Wege laufen durch `verschiebe(von, nach)` und schicken die KOMPLETTE Id-Folge —
 * der Server nimmt genau die vorhandenen Kennungen, jede einmal; eine Teilliste wäre
 * mehrdeutig. Das Ziehen kommt aus `useZiehOrdnung` (Zeigerereignisse, trägt auch mit dem
 * Finger); die Pfeile sind der tastaturfähige Weg und auf dem Phone der einzige bequeme.
 *
 * <h3>Scroll bei Neuzeichnen (Hausregel)</h3>
 * Nach jedem Schreibweg wird die Liste NEU GEZEICHNET, aber nicht neu positioniert: Der
 * Kasten hält die Scrollposition des Fensters und stellt sie wieder her. Ohne das sprang
 * die Ansicht bei jedem Pfeilklick nach oben — und wer die achte Anlage sortiert, verlöre
 * sie aus dem Blick.
 *
 * <h3>Kein `dangerouslySetInnerHTML`</h3>
 * Die Bildschirmvorschau geht durch `renderMarkdown` aus `../markdown.js` — denselben
 * Port, den die Belegtexte benutzen. Ein Anhang ist Text aus einem Textbaustein, den ein
 * Bearbeiter geschrieben hat; er wird gesetzt, nicht eingespritzt.
 */

export type AnhaengeClient = Pick<ApiClient,
  "getAnhaenge" | "anhangAnhaengen" | "anhangEntfernen" | "anhaengeOrdnen"
  | "anhangSichtbarkeit">;

export type AnhaengeProps = {
  client: AnhaengeClient;
  bezug: Anhangsbezug;
  /** Der Schlüssel für Ids in der Maske (`kanh-lese`, `kanh-bearbeiten`). */
  idPraefix: string;
  /**
   * Die Belegversion, die der RAHMEN kennt — die Eingangsrichtung zur Meldung über
   * `onVersionChange` (Fixrunde 1, S1-3; dieselbe Anordnung wie am `PositionsEditor`).
   *
   * Sie ist nötig, weil der Rahmen selbst schreibt: Speichert er seinen Kopf (Laufzeit,
   * Parteien, Zahlplan), steigt die Version des Belegs, ohne dass der Kasten etwas getan
   * hätte. Ohne diesen Weg herein ginge sein nächster Schreibweg mit der alten Zahl hinaus
   * und bekäme einen 409 — der Bearbeiter müsste nach jedem Kopf-Speichern jeden
   * Anhangsschritt zweimal machen.
   *
   * OPTIONAL, anders als am PositionsEditor: Der Kasten lädt seinen Stand selbst und kennt
   * seine Version daraus. Ein Einbauort ohne eigenen Kopf-Schreibweg (die Angebotsvorlage
   * im Lesedetail) braucht sie nicht anzugeben.
   */
  version?: number | undefined;
  /** Lesend: der Kasten im geschlossenen Detail und am eingefrorenen Angebot. */
  nurLesend?: boolean | undefined;
  /** Verdeckt: solange ein Schreibweg des Rahmens läuft. */
  verdeckt?: boolean | undefined;
  /** Sagt dem Rahmen die neue Belegversion nach jedem Schreibweg. */
  onVersionChange?: ((version: number) => void) | undefined;
};

/** Der Kasten bringt KEIN Kopffeld mit — die Anhänge stehen unter dem Zahlplan, nicht im
 *  Kopfraster. Die Tabelle steht trotzdem hier, damit die Detailansichten sie wie die der
 *  Parteien und des Zahlplans streuen können (und beim nächsten Feld nicht zwei Stellen
 *  angefasst werden müssen). */
export const ANHAENGE_KOPFFELDER: Record<string, string> = {};

/**
 * Der Satz für einen Versionskonflikt. Er sagt AUSDRÜCKLICH „erneut", weil der Kasten den
 * Schritt NICHT selbst wiederholt: Ein automatischer zweiter Versuch mit der frischen
 * Version würde die Absicht eines anderen Bearbeiters überschreiben, die man gerade erst
 * geladen hat.
 */
const KONFLIKT_HINWEIS =
  "Dieser Vorgang wurde zwischenzeitlich geändert. Der aktuelle Stand ist neu geladen — "
  + "bitte den Schritt erneut ausführen.";

export function Anhaenge(p: AnhaengeProps): ReactElement {
  const { client, bezug, idPraefix } = p;
  const [stand, setStand] = useState<AnhaengeStand | null>(null);
  const [fehler, setFehler] = useState<string | null>(null);
  const [hinweis, setHinweis] = useState<string | null>(null);
  const [beschaeftigt, setBeschaeftigt] = useState(false);
  const [offen, setOffen] = useState<string | null>(null);
  const [wahlOffen, setWahlOffen] = useState(false);
  /** Die Version, mit der der nächste Schreibweg hinausgeht. */
  const version = useRef(0);
  const schluessel = anhangsSchluessel(bezug);
  const getAnhaenge = client.getAnhaenge;

  /**
   * Der Rückruf an den Rahmen liegt in einem REF und nicht im Abhängigkeitsarray.
   *
   * Der Rahmen gibt ihn als Pfeilfunktion im JSX weiter — bei jedem Zeichnen eine neue.
   * Hinge `uebernehmen` daran, wäre `laden` bei jedem Zeichnen neu, der Ladeeffekt liefe
   * wieder, setzte den Stand, löste das nächste Zeichnen aus: eine Schleife, die erst beim
   * Einbau in K11 aufgefallen wäre und dort als „der Kasten lädt endlos" ankäme. Dieselbe
   * Bindung wie `ablageRef` in `useZiehOrdnung`.
   */
  const versionsRef = useRef(p.onVersionChange);
  versionsRef.current = p.onVersionChange;

  const uebernehmen = useCallback((neu: AnhaengeStand) => {
    setStand(neu);
    version.current = neu.version;
    versionsRef.current?.(neu.version);
  }, []);

  const laden = useCallback(async () => {
    try {
      uebernehmen(await getAnhaenge(bezug));
      setFehler(null);
    } catch (e) {
      setFehler(alsMeldung(e, "Die Anlagen konnten nicht geladen werden."));
    }
    // `schluessel` statt `bezug`: Ein Objektliteral im JSX ist bei jedem Zeichnen ein
    // neues, und ein Effekt, der am OBJEKT hängt, lädt nach jedem Zeichnen neu.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [getAnhaenge, schluessel, uebernehmen]);

  useEffect(() => { void laden(); }, [laden]);

  /**
   * Die Version von AUSSEN (S1-3). Sie überschreibt die eigene ohne Vergleich: Der Rahmen
   * hat den Beleg gerade geschrieben und weiss es genauer. Ein `Math.max` wäre hier falsch
   * — eine Version kann nach dem Verwerfen einer Änderung auch wieder kleiner sein, und der
   * Server entscheidet, nicht die Maske.
   */
  useEffect(() => {
    if (p.version !== undefined) version.current = p.version;
  }, [p.version]);

  /**
   * Der gemeinsame Rahmen JEDES Schreibwegs: Version hinein, frischen Stand heraus, und
   * die SCROLLPOSITION bleibt (Hausregel). 409 heisst nicht „Fehler", sondern „jemand
   * anders war schneller".
   *
   * Der 409 hat ZWEI Gestalten, und der Unterschied ist die zweite Anfrage wert:
   *
   *  - MIT `conflict.current` — der Server hat den frischen Anhangsstand mitgeschickt
   *    (`ConflictError` in den Anhangswegen). Dann wird er übernommen; ein GET danach
   *    holte dasselbe noch einmal.
   *  - OHNE `current` — etwa `ANGEBOT_EINGEFROREN`, das ein Zustandskonflikt und kein
   *    Versionskonflikt ist und deshalb keinen Stand mitführt. Dann wird geladen, denn
   *    ohne frischen Stand stünde die Maske auf einem Bild, das es nicht mehr gibt.
   */
  async function schreibe(
    lauf: (v: number) => Promise<AnhaengeStand>, standard: string,
  ): Promise<void> {
    const scrollY = window.scrollY;
    setBeschaeftigt(true);
    setHinweis(null);
    try {
      uebernehmen(await lauf(version.current));
      setFehler(null);
    } catch (e) {
      if (e instanceof ApiError && e.status === 409) {
        // Die Meldung des Servers hat Vorrang, wenn sie fachlich ist (`ANGEBOT_EINGEFROREN`
        // sagt, WARUM es nicht geht); der reine Versionskonflikt bekommt den Haussatz.
        const frisch = frischerStand(e);
        setHinweis(e.code === "ANGEBOT_EINGEFROREN"
          ? `${e.message} Der aktuelle Stand ist neu geladen — bitte erneut ansehen.`
          : KONFLIKT_HINWEIS);
        if (frisch !== null) uebernehmen(frisch);
        else await laden();
        setFehler(null);
      } else {
        setFehler(alsMeldung(e, standard));
      }
    } finally {
      setBeschaeftigt(false);
      /*
       * Nach dem Neuzeichnen: dieselbe Stelle wie vorher — aber nur, wenn die Seite
       * WIRKLICH gewandert ist. Ein `scrollTo` auf die Stelle, an der man schon steht, ist
       * nicht bloss überflüssig: Es bricht ein sanftes Scrollen des Browsers ab, das gerade
       * anderswo läuft, und meldet sich in jsdom als „Not implemented" im Testprotokoll.
       *
       * Was dieser Riegel NICHT leistet (Fixrunde 1, S3): Steht der Kasten im
       * Bearbeitungs-Modal, rollt nicht das Fenster, sondern `.ki-modal` — dessen
       * Rollstellung fasst hier niemand an. Sie bleibt trotzdem, weil die Liste ihre
       * `key`s behält (die Anhangskennung) und React die Zeilen deshalb verändert statt sie
       * neu zu bauen. Das Fenster-`scrollTo` ist der Fall „Kasten im Lesedetail".
       */
      if (window.scrollY !== scrollY) window.scrollTo({ top: scrollY });
    }
  }

  /** Der EINE Umsortier-Weg, den beide Bedienarten benutzen: Ziehen und die Pfeile. */
  async function verschiebe(von: number, nach: number): Promise<void> {
    const ids = (stand?.anhaenge ?? []).map((a) => a.id);
    if (von < 0 || nach < 0 || von >= ids.length || nach >= ids.length || von === nach) {
      return;
    }
    const geordnet = [...ids];
    const [bewegt] = geordnet.splice(von, 1);
    geordnet.splice(nach, 0, bewegt!);
    await schreibe((v) => client.anhaengeOrdnen(bezug, v, geordnet),
      "Die Reihenfolge konnte nicht gespeichert werden.");
  }

  async function anhaengen(textBlockId: string): Promise<void> {
    await schreibe((v) => client.anhangAnhaengen(bezug, v, textBlockId),
      "Die Anlage konnte nicht angehängt werden.");
    // Das Modal bleibt OFFEN: Wer AGB und Datenschutz anhängt, will zweimal klicken und
    // nicht zweimal den Knopf suchen. Geschlossen wird über „Fertig" oder Escape.
  }

  const { bestaetigen, bestaetigung } = useBestaetigung();
  async function entfernen(anhangId: string, titel: string): Promise<void> {
    // Rückfrage, weil es kein Zurück gibt (Muster `loeschen` in textbausteine.tsx) — der
    // BAUSTEIN bleibt, aber die Anlage ist aus diesem Vorgang fort.
    if (!(await bestaetigen({
      titel: "Anlage entfernen?",
      text: `„${titel}" aus diesem Vorgang entfernen? Der Textbaustein selbst bleibt `
        + "erhalten.",
      bestaetigenText: "Entfernen",
    }))) {
      return;
    }
    await schreibe((v) => client.anhangEntfernen(bezug, anhangId, v),
      "Die Anlage konnte nicht entfernt werden.");
  }

  async function ausblenden(anhangId: string, hidden: boolean): Promise<void> {
    // Der Riegel ist KEINE Förmlichkeit: `anhangSichtbarkeit` führt `customerId` und
    // `vorgangId` ausgeschrieben, weil es den Weg nur am Auftrag gibt. Ein `template`-Bezug
    // hat gar keine Kundenkennung.
    if (bezug.art !== "order") return;
    await schreibe(
      (v) => client.anhangSichtbarkeit(bezug.customerId, bezug.vorgangId, anhangId, v,
        hidden),
      "Der Schalter konnte nicht gespeichert werden.");
  }

  const amAuftrag = bezug.art === "order";
  const lesend = p.nurLesend === true || stand?.eingefroren === true || amAuftrag;
  const gesperrt = beschaeftigt || p.verdeckt === true;

  const ordnung = useZiehOrdnung(stand?.anhaenge.length ?? 0, (von, nach) => {
    // DER ZWEITE RIEGEL (Fixrunde 1, S2-6). Der erste sitzt am Griff (er nimmt gesperrt
    // gar keinen Zug an); dieser hier fängt den Fall, dass die Sperre WÄHREND eines
    // laufenden Zugs kommt — dann liefe die Ablage sonst mitten in einen Schreibweg des
    // Rahmens und schickte die alte Version.
    if (gesperrt) return;
    void verschiebe(von, nach);
  });

  /**
   * Die Eigenschaften des Griffs — und, solange gesperrt ist, ein Griff, der NICHTS tut
   * (Fixrunde 1, S2-6).
   *
   * Vorher sperrte `verdeckt` nur die Knöpfe. Der Griff lag daneben und nahm einen Zug an,
   * dessen Ablage dann mit der Version von vor dem Schreibweg des Rahmens hinausging. Das
   * `aria-disabled` ist dabei nicht der Riegel, sondern seine ANSAGE — der Riegel ist der
   * ersetzte `onPointerDown`, und das CSS nimmt dem Griff zusätzlich den Zeiger ab, damit
   * gar kein Ereignis entsteht.
   */
  function griffEigenschaften(index: number): Record<string, unknown> {
    const eigen = ordnung.griff(index);
    if (!gesperrt) return eigen;
    return {
      ...eigen,
      "aria-disabled": "true",
      onPointerDown: (e: ReactPointerEvent) => { e.preventDefault(); },
    };
  }

  return (
    <div className="kanh__kasten" id={`${idPraefix}-anhaenge`}>
      <div className="kanh__kopf">
        <h3 className="kanh__titel">Anhänge</h3>
        {/* `stand === null`: Ohne geladenen Stand gibt es keine Bausteinliste und keine
            Version — der Knopf öffnete ein leeres Modal (Fixrunde 1, S3). */}
        {!lesend && (
          <button type="button" className="kanh__sekundaer"
            disabled={gesperrt || stand === null}
            onClick={() => setWahlOffen(true)}>
            Baustein anhängen
          </button>
        )}
      </div>

      {/*
        * SCHLECHTESTES OBEN (Hausregel): der Fehler VOR der Liste, der Hinweis darunter.
        *
        * Solange das Auswahl-Modal offen ist, stehen beide DORT und nicht hier (Fixrunde 1,
        * S1-2). Das Modal legt sich über den Kasten; eine Meldung hier unten wäre hinter
        * dem eigenen Panel unsichtbar, und der Bearbeiter klickte, weil scheinbar nichts
        * geschah, ein zweites Mal. „Schlechtestes oben" heisst deshalb auch: auf der
        * richtigen EBENE — und genau EINMAL, damit ein Vorlesewerkzeug nicht zwei
        * Meldungen mit demselben Wortlaut findet.
        */}
      {!wahlOffen && fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
      {!wahlOffen && hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}

      {stand === null && fehler === null && (
        <p className="kanh__leer" role="status">Lädt …</p>
      )}
      {stand !== null && stand.anhaenge.length === 0 && (
        <p className="kanh__leer">
          {lesend
            ? "Dieser Vorgang führt keine Anlagen."
            : "Noch keine Anlage. Über „Baustein anhängen“ kommen AGB, Preisblätter oder "
              + "Datenschutzhinweise als Folgeseiten in den Beleg."}
        </p>
      )}

      {stand !== null && stand.anhaenge.length > 0 && (
        <ul className="kanh__liste" aria-label="Anlagen">
          {stand.anhaenge.map((a, index) => (
            <li key={a.id} className="kanh__zeile" ref={ordnung.merke(index)}
              {...ordnung.zeile(index)}
              {...(a.hidden && amAuftrag ? { "data-versteckt": "" } : {})}>
              {!lesend && (
                <span className="kanh__griff" {...griffEigenschaften(index)}>⠿</span>
              )}
              <div className="kanh__inhalt">
                <button type="button" className="kanh__aufklapper"
                  aria-expanded={offen === a.id}
                  /* Titel UND Richtung im zugänglichen Namen (Fixrunde 1, S3): Ein Knopf,
                     der offen wie zu „anzeigen" heisst, sagt einem Vorlesewerkzeug nichts
                     über den Zustand — und ohne den Titel heissen bei zehn Anlagen zehn
                     Knöpfe fast gleich. */
                  aria-label={`Anlage ${String(a.sortOrder)}: ${a.titel} `
                    + (offen === a.id ? "zuklappen" : "anzeigen")}
                  onClick={() => setOffen(offen === a.id ? null : a.id)}>
                  <span className="kanh__nummer">{`Anlage ${String(a.sortOrder)}`}</span>
                  <span className="kanh__anlagentitel">{a.titel}</span>
                </button>
                <p className="kanh__herkunft">
                  {/* Das DATUM nur am Angebot und an der Vorlage (Fixrunde 1, S3): Am
                      Auftrag wandert `updatedAt` mit jedem Ausblenden-Schalter mit, und
                      Migration 031 führt kein eigenes `frozenAt` — ein Datum dort
                      behauptete einen Zeitpunkt, den die Spalte nicht kennt. */}
                  {a.herkunft !== null
                    ? `aus Baustein „${a.herkunft.name}" — Änderungen dort wirken hier mit`
                    : amAuftrag
                      ? "eingefroren — der Text steht als Schnappschuss im Vorgang"
                      // Kurz, weil der Satz unter dem Kasten das „eingefroren" schon
                      // erklärt (Dirk 28.09.2026: nicht zweimal dasselbe).
                      : `Schnappschuss vom ${formatDatum(a.updatedAt)}`}
                  {a.hidden && amAuftrag
                    ? " · auf der Auftragsbestätigung ausgeblendet"
                    : ""}
                </p>
                {offen === a.id && (
                  <div className="kanh__text">
                    {renderMarkdown(ohneTitelzeile(a.markdown))}
                  </div>
                )}
              </div>
              <div className="kanh__aktionen">
                {!lesend && (
                  <>
                    {/* Der zweite, GLEICHWERTIGE Weg zum Umsortieren (Entscheid E8):
                        tastaturfähig, beschriftet, und auf dem Phone der einzige
                        bequeme. */}
                    <button type="button" className="kanh__klein"
                      disabled={gesperrt || index === 0}
                      aria-label={`Anlage ${String(a.sortOrder)} nach oben`}
                      onClick={() => { void verschiebe(index, index - 1); }}>▲</button>
                    <button type="button" className="kanh__klein"
                      disabled={gesperrt || index === stand.anhaenge.length - 1}
                      aria-label={`Anlage ${String(a.sortOrder)} nach unten`}
                      onClick={() => { void verschiebe(index, index + 1); }}>▼</button>
                    <button type="button" className="kanh__klein" disabled={gesperrt}
                      aria-label={`Anlage ${String(a.sortOrder)} entfernen`}
                      onClick={() => { void entfernen(a.id, a.titel); }}>Entfernen</button>
                  </>
                )}
                {/* Ein echtes Ja/Nein, das sofort schreibt — `me-schalter` (ME-Etappe 3,
                    Aufgabe 4). Die Nummer steht in der Beschriftung: Zehn Schalter mit
                    demselben Namen wären für ein Vorlesewerkzeug nicht zu unterscheiden.
                    `gesperrt` statt `disabled`: Der Fokus bleibt während des Schreibens. */}
                {amAuftrag && (
                  <Schalter className="kanh__schalter" name={`${p.idPraefix}-aus-${a.id}`}
                    beschriftung={`Anlage ${String(a.sortOrder)} auf der `
                      + "Auftragsbestätigung ausblenden"}
                    an={a.hidden} gesperrt={gesperrt}
                    onGeaendert={(d) => { void ausblenden(a.id, d.an); }} />
                )}
              </div>
            </li>
          ))}
        </ul>
      )}

      {/* Am eingefrorenen Angebot sagt der Kasten, WARUM er lesend ist — sonst sucht jemand
          den Knopf (Lehre der Doku-Vormerkungen zu den älteren Angebotsversionen). */}
      {stand?.eingefroren === true && !amAuftrag && (
        <p className="kanh__leer">
          Dieses Angebot ist gesendet — seine Anlagen sind eingefroren und zeigen den Text
          von damals. Für eine geänderte Fassung eine neue Version anlegen.
        </p>
      )}

      {wahlOffen && stand !== null && (
        <Modal titel="Baustein anhängen" breite="mittel"
          onAbbrechen={() => setWahlOffen(false)} abbrechenGesperrt={beschaeftigt}>
          {/* Die Meldungen des laufenden Anhängens — hier und nicht im Kasten darunter
              (Fixrunde 1, S1-2). */}
          {fehler !== null && <Hinweis art="fehler">{fehler}</Hinweis>}
          {hinweis !== null && <Hinweis art="warnung">{hinweis}</Hinweis>}
          {stand.bausteine.length === 0 && (
            <p className="kanh__leer">
              Für dieses Unternehmen ist noch kein Textbaustein gepflegt. Sie entstehen in
              der Verwaltung unter „Textbausteine".
            </p>
          )}
          <ul className="kanh__wahl">
            {stand.bausteine.map((b) => (
              <li key={b.id}>
                <button type="button" className="kanh__wahlzeile"
                  disabled={b.angehaengt || gesperrt}
                  aria-label={`${b.name} anhängen`}
                  onClick={() => { void anhaengen(b.id); }}>
                  <span className="kanh__wahlname">{b.name}</span>
                  {b.uebergreifend && (
                    <span className="kanh__marke">für alle Unternehmen</span>
                  )}
                  {b.angehaengt && <span className="kanh__marke">schon angehängt</span>}
                </button>
              </li>
            ))}
          </ul>
          {/*
            * EIN Knopf, und er heisst „Schliessen" (Fixrunde 1, S3).
            *
            * Es gibt hier nichts zu verwerfen: Jeder Klick auf einen Baustein ist schon
            * gespeichert. „Abbrechen" versprach eine Rücknahme, die es nicht gibt — und die
            * Hausregel „nur Speichern/Abbrechen" gilt für ENTWURFS-Dialoge, also für Masken
            * mit ungeschriebenem Inhalt. Escape geht denselben Weg wie der Knopf.
            */}
          <ModalFuss speichernText="Schliessen" speichernGesperrt={beschaeftigt}
            nurSchliessen
            onSpeichern={() => setWahlOffen(false)}
            onAbbrechen={() => setWahlOffen(false)} />
        </Modal>
      )}
      {bestaetigung}
    </div>
  );
}

/**
 * Der frische Anhangsstand aus einem 409, falls der Server ihn mitgeschickt hat.
 *
 * Geprüft wird die GESTALT und nicht nur das Vorhandensein: `conflict.current` ist im
 * Klienten `unknown`, weil derselbe Fehlertyp alle Wege trägt — ein Zahlplanstand aus einem
 * anderen Weg dürfte hier nicht als Anhangsstand durchgehen. Passt die Gestalt nicht, wird
 * geladen; das ist eine Anfrage mehr und nie ein falsches Bild.
 */
function frischerStand(fehler: ApiError): AnhaengeStand | null {
  const roh = fehler.conflict?.current;
  if (roh === null || typeof roh !== "object") return null;
  const k = roh as Partial<AnhaengeStand>;
  if (typeof k.version !== "number" || typeof k.eingefroren !== "boolean") return null;
  if (!Array.isArray(k.anhaenge) || !Array.isArray(k.bausteine)) return null;
  return k as AnhaengeStand;
}

/** Eine ApiError-Meldung herausziehen, sonst ein verständlicher Standardtext (Muster
 *  `alsMeldung` in PositionsEditor.tsx). Der FELDBEFUND hat Vorrang: Bei
 *  `ANHANG_SCHON_VORHANDEN` steht die verständliche Aussage am Feld `textBlockId`, während
 *  die Kopfmeldung nur „nicht möglich" sagt. */
function alsMeldung(fehler: unknown, standard: string): string {
  if (fehler instanceof ApiError) {
    const feld = fehler.fields?.[0]?.message;
    if (feld !== undefined && feld.trim() !== "") return feld;
    return fehler.message.trim() === "" ? standard : fehler.message;
  }
  return standard;
}

/**
 * Schneidet die erste `# `-Zeile weg, wenn es eine gibt: Sie IST der Anlagentitel und
 * steht als Zeilenüberschrift darüber. Im PDF macht die Engine dasselbe
 * (`AnhangMarkdown`), und zwei verschiedene Antworten auf „steht die Überschrift zweimal?"
 * wären der Unterschied, den ein Bearbeiter erst im Druck sieht.
 *
 * Die REGEL steht in `domain` (`anhangTitel`) — hier steht nur ihre Umkehrung, und
 * `packages/web` darf `domain` nicht einführen (check-boundaries). Sie ist klein genug,
 * dass eine zweite Fassung vertretbar ist; die Angleichungs-Spec hält beide fest.
 */
function ohneTitelzeile(markdown: string): string {
  // Auch ein EINZELNES `\r` ist ein Zeilenende (alte Mac-Dateien, aus manchen Programmen
  // kopierte Blöcke) — `anhangTitel` in `domain` normalisiert genauso. Ohne den zweiten
  // Schritt stand die Überschrift in der Bildschirmvorschau doppelt, während das PDF sie
  // einmal zeigte (Fixrunde 1, S3).
  const zeilen = markdown.replace(/\r\n/g, "\n").replace(/\r/g, "\n").split("\n");
  const erste = zeilen.findIndex((z) => z.trim() !== "");
  if (erste < 0 || !/^#[ \t]+\S/.test(zeilen[erste]!.trim())) return markdown;
  return zeilen.slice(erste + 1).join("\n");
}
